import { Component, Inject } from '@angular/core';
import { FormBuilder, Validators } from '@angular/forms';
import { MAT_DIALOG_DATA, MatDialogRef } from '@angular/material/dialog';

type TaskFilter = 'all' | 'standalone' | number;

export interface TaskCreateDialogData {
  projects: any[];
  staffs: any[];
  topLevelTasks: any[];
  projectFilter: TaskFilter;
  presetParentTask?: any | null;
  canAssignAnyStaff: boolean;
  currentStaffId: number | null;
}

@Component({
  selector: 'app-task-create-dialog',
  templateUrl: './task-create-dialog.component.html',
  styleUrls: ['./task-create-dialog.component.scss']
})
export class TaskCreateDialogComponent {
  form = this.fb.group({
    title: ['', Validators.required],
    description: [''],
    priority: ['Medium', Validators.required],
    assignedStaffId: [this.initialAssignedStaffId()],
    dueDate: [''],
    isStandalone: [this.initialStandaloneValue()],
    projectId: [this.initialProjectId()],
    parentTaskId: [this.data.presetParentTask?.id || null]
  });

  priorityOptions = ['Low', 'Medium', 'High', 'Critical'];

  constructor(
    private fb: FormBuilder,
    @Inject(MAT_DIALOG_DATA) public data: TaskCreateDialogData,
    public dialogRef: MatDialogRef<TaskCreateDialogComponent>
  ) {}

  get filteredParentTasks(): any[] {
    const isStandalone = !!this.form.value.isStandalone;
    const projectId = this.normalizeProjectId(this.form.value.projectId);
    return (this.data.topLevelTasks || []).filter((task) => {
      if (this.data.presetParentTask?.id && task.id === this.data.presetParentTask.id) return true;
      if (isStandalone) return !task.projectId;
      return !!projectId && task.projectId === projectId;
    });
  }

  save(): void {
    if (this.form.invalid) {
      this.form.markAllAsTouched();
      return;
    }

    const parentTaskId = this.normalizeNumber(this.form.value.parentTaskId);
    const parentTask = (this.data.topLevelTasks || []).find((task) => task.id === parentTaskId) || this.data.presetParentTask || null;
    const isStandalone = parentTask ? !parentTask.projectId : !!this.form.value.isStandalone;
    const projectId = parentTask ? (parentTask.projectId || null) : (isStandalone ? null : this.normalizeProjectId(this.form.value.projectId));

    if (!isStandalone && !projectId) {
      this.form.get('projectId')?.markAsTouched();
      return;
    }

    this.dialogRef.close({
      title: this.form.value.title?.trim(),
      description: this.form.value.description || null,
      priority: this.form.value.priority || 'Medium',
      assignedStaffId: this.data.canAssignAnyStaff ? this.normalizeNumber(this.form.value.assignedStaffId) : this.data.currentStaffId,
      dueDate: this.form.value.dueDate || null,
      projectId,
      parentTaskId
    });
  }

  onStandaloneChange(): void {
    if (this.form.value.isStandalone) {
      this.form.patchValue({ projectId: null, parentTaskId: null }, { emitEvent: false });
      return;
    }

    if (typeof this.data.projectFilter === 'number') {
      this.form.patchValue({ projectId: this.data.projectFilter }, { emitEvent: false });
    }
  }

  onProjectChange(): void {
    const parentTaskId = this.normalizeNumber(this.form.value.parentTaskId);
    if (!parentTaskId) return;

    const selectedParent = (this.data.topLevelTasks || []).find((task) => task.id === parentTaskId);
    if (selectedParent && selectedParent.projectId !== this.normalizeProjectId(this.form.value.projectId)) {
      this.form.patchValue({ parentTaskId: null }, { emitEvent: false });
    }
  }

  private initialAssignedStaffId(): number | null {
    return this.data.canAssignAnyStaff ? null : this.data.currentStaffId;
  }

  private initialStandaloneValue(): boolean {
    if (this.data.presetParentTask) return !this.data.presetParentTask.projectId;
    return this.data.projectFilter === 'standalone';
  }

  private initialProjectId(): number | null {
    if (this.data.presetParentTask?.projectId) return this.data.presetParentTask.projectId;
    return typeof this.data.projectFilter === 'number' ? this.data.projectFilter : null;
  }

  private normalizeNumber(value: any): number | null {
    const parsed = Number(value);
    return Number.isFinite(parsed) && parsed > 0 ? parsed : null;
  }

  private normalizeProjectId(value: any): number | null {
    return this.normalizeNumber(value);
  }
}
